import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X,
  Clock,
  CreditCard,
  Cpu,
  CheckCircle,
  Truck,
  Package,
  AlertTriangle,
  ExternalLink,
  Mail,
  Printer,
  Eye,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type {
  Order,
  OrderStatus,
  Profile,
  Workbook,
  WorkbookProfile,
  DeliveryLog,
  GenerationMetadata,
  GenerationWarning,
} from '@ellie/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrderWithRelations extends Order {
  profiles: Pick<Profile, 'full_name' | 'email'> | null;
  workbook_profiles: Pick<
    WorkbookProfile,
    'display_name' | 'native_language' | 'target_language' | 'level' | 'pace'
  > | null;
  workbooks: Pick<
    Workbook,
    | 'id'
    | 'generation_model'
    | 'generation_duration_ms'
    | 'generation_metadata'
    | 'generation_warnings'
    | 'block_count'
    | 'page_count'
  >[];
}

interface Props {
  orderId: string;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Timeline stage definitions
// ---------------------------------------------------------------------------

interface TimelineStage {
  key: string;
  label: string;
  icon: React.ReactNode;
  /** ISO timestamp for this stage, or null if not reached. */
  timestamp: string | null;
  /** Statuses that mean we're at or past this stage. */
  reachedStatuses: OrderStatus[];
}

function buildStages(order: OrderWithRelations): TimelineStage[] {
  // All post-preview statuses — used when the order went through preview flow
  const allPostPreview: OrderStatus[] = [
    'preview',
    'pending',
    'paid',
    'generating',
    'review',
    'approved',
    'delivering',
    'delivered',
    'rejected',
    'failed',
    'refunded',
  ];

  return [
    {
      key: 'preview_generated',
      label: 'Preview Generated',
      icon: <Eye size={16} />,
      timestamp: order.preview_generated_at,
      // Only reached if this order actually had a preview generated
      reachedStatuses: order.preview_generated_at ? allPostPreview : [],
    },
    {
      key: 'created',
      label: 'Order Created',
      icon: <Package size={16} />,
      timestamp: order.checkout_initiated_at ?? null,
      reachedStatuses: [
        'pending',
        'paid',
        'generating',
        'review',
        'approved',
        'delivering',
        'delivered',
        'rejected',
        'failed',
        'refunded',
      ],
    },
    {
      key: 'paid',
      label: 'Payment Received',
      icon: <CreditCard size={16} />,
      timestamp: order.paid_at,
      reachedStatuses: [
        'paid',
        'generating',
        'review',
        'approved',
        'delivering',
        'delivered',
        'rejected',
        'failed',
      ],
    },
    {
      key: 'generated',
      label: 'Workbook Generated',
      icon: <Cpu size={16} />,
      timestamp: order.generated_at,
      reachedStatuses: ['review', 'approved', 'delivering', 'delivered', 'rejected'],
    },
    {
      key: 'approved',
      label: 'Approved for Delivery',
      icon: <CheckCircle size={16} />,
      timestamp: order.approved_at,
      reachedStatuses: ['approved', 'delivering', 'delivered'],
    },
    {
      key: 'delivered',
      label: 'Delivered',
      icon: <Truck size={16} />,
      timestamp: order.delivered_at,
      reachedStatuses: ['delivered'],
    },
  ];
}

/** Which stage is the order actively at, based on status? */
const STATUS_TO_ACTIVE_STAGE: Partial<Record<OrderStatus, string>> = {
  preview: 'preview_generated',
  pending: 'created',
  paid: 'paid',
  generating: 'paid', // waiting for generation to complete
  review: 'generated',
  approved: 'approved',
  delivering: 'approved', // waiting for delivery to complete
  delivered: 'delivered',
  failed: 'paid',
  rejected: 'generated',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(ts: string): string {
  return new Date(ts).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3_600_000) {
    const mins = Math.floor(ms / 60_000);
    const secs = Math.round((ms % 60_000) / 1000);
    return `${mins}m ${secs}s`;
  }
  const hours = Math.floor(ms / 3_600_000);
  const mins = Math.round((ms % 3_600_000) / 60_000);
  return `${hours}h ${mins}m`;
}

function durationBetween(a: string, b: string): string {
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return formatDuration(ms);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OrderJourneyDrawer({ orderId, onClose }: Props) {
  const [order, setOrder] = useState<OrderWithRelations | null>(null);
  const [deliveryLogs, setDeliveryLogs] = useState<DeliveryLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      const [orderRes, logsRes] = await Promise.all([
        supabase
          .from('orders')
          .select(
            `*, profiles(full_name, email), workbook_profiles(display_name, native_language, target_language, level, pace), workbooks(id, generation_model, generation_duration_ms, generation_metadata, generation_warnings, block_count, page_count)`,
          )
          .eq('id', orderId)
          .single(),
        supabase
          .from('delivery_log')
          .select('*')
          .eq('order_id', orderId)
          .order('created_at', { ascending: true }),
      ]);

      if (orderRes.data) {
        setOrder(orderRes.data as unknown as OrderWithRelations);
      }
      setDeliveryLogs((logsRes.data as DeliveryLog[]) ?? []);
      setLoading(false);
    }

    fetchData();
  }, [orderId]);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col overflow-y-auto bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="font-heading text-lg text-text-primary">Order Journey</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-text-muted transition-colors hover:bg-gray-100 hover:text-text-primary"
          >
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
          </div>
        ) : !order ? (
          <div className="p-5 text-sm text-red-600">Order not found.</div>
        ) : (
          <div className="flex-1 space-y-6 p-5">
            {/* Order summary */}
            <div className="rounded-lg border border-border bg-gray-50 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-primary">
                    {order.profiles?.full_name ?? 'Unknown'}
                  </p>
                  <p className="text-xs text-text-muted">{order.profiles?.email ?? ''}</p>
                </div>
                <StatusBadge status={order.status} />
              </div>
              <div className="mt-3 flex gap-4 text-xs text-text-muted">
                <span className="font-mono">{order.id.slice(0, 8)}</span>
                <span className="capitalize">{order.delivery_method}</span>
                {order.workbook_profiles && (
                  <span>
                    {order.workbook_profiles.native_language.toUpperCase()} →{' '}
                    {order.workbook_profiles.target_language.toUpperCase()}{' '}
                    {order.workbook_profiles.level}
                  </span>
                )}
              </div>
            </div>

            {/* Timeline */}
            <Timeline order={order} />

            {/* Generation details — preview and/or full workbook */}
            {order.preview_generation_metadata && (
              <GenerationMetadataCard
                title="Preview Generation"
                metadata={order.preview_generation_metadata as GenerationMetadata}
              />
            )}
            {order.workbooks?.[0] && <GenerationCard workbook={order.workbooks[0]} />}

            {/* Delivery log */}
            {deliveryLogs.length > 0 && <DeliveryLogSection logs={deliveryLogs} />}

            {/* Link to workbook editor */}
            {order.workbooks?.[0] && (
              <Link
                to={`/workbook/${order.workbooks[0].id}`}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
              >
                <ExternalLink size={14} />
                Open in Workbook Editor
              </Link>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Timeline
// ---------------------------------------------------------------------------

function Timeline({ order }: { order: OrderWithRelations }) {
  const stages = buildStages(order);
  const activeStageKey = STATUS_TO_ACTIVE_STAGE[order.status];

  return (
    <section>
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-muted">
        Timeline
      </h3>
      <div className="relative">
        {stages.map((stage, i) => {
          const isReached = stage.reachedStatuses.includes(order.status);
          const isActive = stage.key === activeStageKey;
          const prevStage = i > 0 ? stages[i - 1] : null;
          const showDuration = stage.timestamp && prevStage?.timestamp;

          return (
            <div key={stage.key} className="flex gap-3">
              {/* Vertical line + dot */}
              <div className="flex flex-col items-center">
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${
                    isActive
                      ? 'animate-pulse border-brand bg-brand/10 text-brand'
                      : isReached
                        ? 'border-green-500 bg-green-50 text-green-600'
                        : 'border-gray-200 bg-gray-50 text-gray-400'
                  }`}
                >
                  {stage.icon}
                </div>
                {i < stages.length - 1 && (
                  <div
                    className={`w-0.5 flex-1 ${isReached ? 'bg-green-300' : 'bg-gray-200'}`}
                    style={{ minHeight: '2rem' }}
                  />
                )}
              </div>

              {/* Content */}
              <div className="pb-5">
                <p
                  className={`text-sm font-medium ${
                    isReached ? 'text-text-primary' : 'text-text-light'
                  }`}
                >
                  {stage.label}
                  {isActive && order.status === 'generating' && stage.key === 'paid' && (
                    <span className="ml-2 text-xs font-normal text-purple-600">Generating...</span>
                  )}
                  {isActive && order.status === 'delivering' && stage.key === 'approved' && (
                    <span className="ml-2 text-xs font-normal text-orange-600">Delivering...</span>
                  )}
                </p>
                {stage.timestamp && (
                  <p className="text-xs text-text-muted">{formatTimestamp(stage.timestamp)}</p>
                )}
                {showDuration && (
                  <p className="mt-0.5 text-[11px] text-text-light">
                    <Clock size={10} className="mr-0.5 inline" />
                    {durationBetween(prevStage!.timestamp!, stage.timestamp!)}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Total end-to-end time */}
      {order.delivered_at && (
        <div className="mt-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs font-medium text-green-700">
          Total: {durationBetween(order.created_at, order.delivered_at)}
        </div>
      )}

      {/* Failed/rejected indicator */}
      {(order.status === 'failed' ||
        order.status === 'rejected' ||
        order.status === 'refunded') && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
          {order.status === 'failed' && 'Generation failed — can be retried from Orders page'}
          {order.status === 'rejected' && 'Workbook rejected by admin'}
          {order.status === 'refunded' && 'Order was refunded'}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Generation Card
// ---------------------------------------------------------------------------

type WorkbookSummary = Pick<
  Workbook,
  | 'id'
  | 'generation_model'
  | 'generation_duration_ms'
  | 'generation_metadata'
  | 'generation_warnings'
  | 'block_count'
  | 'page_count'
>;

/**
 * Collapsible card showing generation metadata from a JSONB snapshot.
 * Used for preview generation details (stored on the order row).
 */
function GenerationMetadataCard({
  title,
  metadata,
}: {
  title: string;
  metadata: GenerationMetadata;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <section>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="mb-3 flex w-full items-center justify-between"
      >
        <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">{title}</h3>
        {expanded ? (
          <ChevronUp size={14} className="text-text-muted" />
        ) : (
          <ChevronDown size={14} className="text-text-muted" />
        )}
      </button>

      {expanded && (
        <div className="rounded-lg border border-border bg-white p-4">
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
            <Stat label="Model" value={metadata.model} />
            <Stat label="Provider" value={metadata.provider ?? '—'} />
            <Stat label="Duration" value={formatDuration(metadata.duration_ms)} />
            <Stat label="Cost" value={`$${metadata.estimated_cost_usd.toFixed(4)}`} />
            <Stat
              label="Tokens"
              value={`${metadata.input_tokens.toLocaleString()} in / ${metadata.output_tokens.toLocaleString()} out`}
            />
          </div>

          {metadata.blocks_succeeded != null && metadata.blocks_total != null && (
            <div className="mt-3 border-t border-border pt-3">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-text-muted">Block success:</span>
                <span
                  className={`font-medium ${
                    metadata.blocks_succeeded === metadata.blocks_total
                      ? 'text-green-600'
                      : 'text-amber-600'
                  }`}
                >
                  {metadata.blocks_succeeded}/{metadata.blocks_total}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function GenerationCard({ workbook }: { workbook: WorkbookSummary }) {
  const meta = workbook.generation_metadata as GenerationMetadata | null;
  const warnings = (workbook.generation_warnings ?? []) as GenerationWarning[];
  const [expanded, setExpanded] = useState(true);

  return (
    <section>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="mb-3 flex w-full items-center justify-between"
      >
        <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          Generation Details
        </h3>
        {expanded ? (
          <ChevronUp size={14} className="text-text-muted" />
        ) : (
          <ChevronDown size={14} className="text-text-muted" />
        )}
      </button>

      {expanded && (
        <div className="rounded-lg border border-border bg-white p-4">
          {/* Summary row */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
            <Stat label="Model" value={workbook.generation_model ?? '—'} />
            <Stat
              label="Duration"
              value={
                workbook.generation_duration_ms
                  ? formatDuration(workbook.generation_duration_ms)
                  : '—'
              }
            />
            {meta && (
              <>
                <Stat label="Cost" value={`$${meta.estimated_cost_usd.toFixed(4)}`} />
                <Stat
                  label="Tokens"
                  value={`${meta.input_tokens.toLocaleString()} in / ${meta.output_tokens.toLocaleString()} out`}
                />
              </>
            )}
            <Stat label="Blocks" value={`${workbook.block_count ?? '—'}`} />
            <Stat label="Pages" value={`${workbook.page_count ?? '—'}`} />
          </div>

          {/* Block health */}
          {meta && meta.blocks_succeeded != null && meta.blocks_total != null && (
            <div className="mt-3 border-t border-border pt-3">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-text-muted">Block success:</span>
                <span
                  className={`font-medium ${
                    meta.blocks_succeeded === meta.blocks_total
                      ? 'text-green-600'
                      : 'text-amber-600'
                  }`}
                >
                  {meta.blocks_succeeded}/{meta.blocks_total}
                </span>
                {meta.block_retries != null && meta.block_retries > 0 && (
                  <span className="text-text-light">({meta.block_retries} retries)</span>
                )}
              </div>

              {/* Back matter status */}
              {meta.back_matter && (
                <div className="mt-1.5 flex gap-3 text-xs">
                  <BackMatterBadge label="Verbs" status={meta.back_matter.verb_glossary} />
                  <BackMatterBadge label="Cultural" status={meta.back_matter.cultural_notes} />
                  <BackMatterBadge label="Welcome" status={meta.back_matter.welcome_page} />
                </div>
              )}
            </div>
          )}

          {/* Batch timings */}
          {meta?.batch_timings_ms && meta.batch_timings_ms.length > 0 && (
            <div className="mt-3 border-t border-border pt-3">
              <p className="mb-2 text-xs font-medium text-text-muted">Batch Timings</p>
              <div className="space-y-1">
                {meta.batch_timings_ms.map((ms, i) => {
                  const maxMs = Math.max(...meta.batch_timings_ms!);
                  const widthPct = maxMs > 0 ? (ms / maxMs) * 100 : 0;
                  return (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="w-14 shrink-0 text-text-muted">Batch {i + 1}</span>
                      <div className="h-4 flex-1 overflow-hidden rounded bg-gray-100">
                        <div
                          className="h-full rounded bg-brand/60"
                          style={{ width: `${widthPct}%` }}
                        />
                      </div>
                      <span className="w-12 shrink-0 text-right font-mono text-text-muted">
                        {(ms / 1000).toFixed(1)}s
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="mt-3 border-t border-border pt-3">
              <p className="mb-2 flex items-center gap-1 text-xs font-medium text-amber-600">
                <AlertTriangle size={12} />
                {warnings.length} Warning{warnings.length !== 1 ? 's' : ''}
              </p>
              <div className="space-y-1">
                {warnings.map((w, i) => (
                  <div
                    key={i}
                    className="rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800"
                  >
                    <span className="font-medium">{w.component}:</span> {w.message}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!meta && (
            <p className="mt-3 text-xs italic text-text-light">
              No cost data — generated before tracking was added.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Delivery Log Section
// ---------------------------------------------------------------------------

function DeliveryLogSection({ logs }: { logs: DeliveryLog[] }) {
  return (
    <section>
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-muted">
        Delivery Log
      </h3>
      <div className="space-y-2">
        {logs.map((log) => (
          <div
            key={log.id}
            className="flex items-start gap-3 rounded-lg border border-border bg-white px-4 py-3"
          >
            <div className="mt-0.5">
              {log.type === 'email' ? (
                <Mail size={14} className="text-blue-500" />
              ) : (
                <Printer size={14} className="text-orange-500" />
              )}
            </div>
            <div className="flex-1 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-medium capitalize text-text-primary">{log.type}</span>
                <DeliveryStatusBadge status={log.status} />
              </div>
              <p className="mt-0.5 text-text-muted">{formatTimestamp(log.created_at)}</p>
              {log.external_id && (
                <p className="mt-0.5 font-mono text-text-light">ID: {log.external_id}</p>
              )}
              {log.error_message && <p className="mt-1 text-red-600">{log.error_message}</p>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-text-muted">{label}: </span>
      <span className="font-medium text-text-primary">{value}</span>
    </div>
  );
}

function BackMatterBadge({ label, status }: { label: string; status: 'ok' | 'failed' }) {
  return (
    <span
      className={`inline-flex items-center gap-0.5 ${
        status === 'ok' ? 'text-green-600' : 'text-red-600'
      }`}
    >
      {status === 'ok' ? <CheckCircle size={10} /> : <AlertTriangle size={10} />}
      {label}
    </span>
  );
}

function DeliveryStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-700',
    sent: 'bg-blue-100 text-blue-700',
    delivered: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  };
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${
        colors[status] ?? 'bg-gray-100 text-gray-700'
      }`}
    >
      {status}
    </span>
  );
}

const STATUS_COLORS: Record<string, string> = {
  preview: 'bg-indigo-100 text-indigo-700',
  pending: 'bg-gray-100 text-gray-700',
  paid: 'bg-blue-100 text-blue-700',
  generating: 'bg-purple-100 text-purple-700',
  review: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  delivering: 'bg-orange-100 text-orange-700',
  delivered: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-700',
  refunded: 'bg-gray-100 text-gray-500',
};

function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-700';
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${color}`}
    >
      {status}
    </span>
  );
}
